import { useEffect, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box } from "@mui/system";
import { Typography } from "@mui/material";
import { useGetTask, useUpdateTasksBookmarks } from "services/tasks";
import { useGetFamilyDetails } from "services/advisor";
import Loading from "components/common/Loading";
import useIsMobile from "components/hooks/useIsMobile";
import TaskDetailsPanel from "components/tasks/TaskDetailsPanel";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Task } from "protogen/tasks_pb";
import NotFound from "components/NotFound";
import TaskDropDown from "components/tasks/TaskDropDown";
import useTitle from "components/hooks/useTitle";
import TwoPanelGrid, { Panel } from "components/details-page/TwoPanelGrid";
import ActivityFeed from "components/activity/ActivityFeed";
import SuggestionActions, {
  SuggestionActionsHandle,
} from "components/activity/SuggestionActions";

import TaskProjectLabels from "components/tasks/TaskProjectLabels";
import { FeedEntry } from "protogen/advisors_service_pb";

export default ({ addTask = false }: { addTask?: boolean }) => {
  const location = useLocation();
  const family = location.state?.family;
  const inspiredTask = location.state?.inspiredTask;
  const unownedTask = location.state?.unownedTask;
  let params = useParams();
  const [task, setTask] = useState<Task | null>(null);
  const [isUnsavedTask, setIsUnsavedTask] = useState(false);
  const [familyData, setFamilyData] = useState<any>(null);
  const [selectedMedium] = useState<any>(null);
  const actionsRef = useRef<SuggestionActionsHandle | null>(null);
  const { request: updateBookmarks } = useUpdateTasksBookmarks();

  const { data, loading, request, error } = useGetTask((r) => {
    setIsUnsavedTask(false);
    setTask(r.task);
  });

  const { request: requestFamilyDetails } = useGetFamilyDetails((r) => {
    setFamilyData(r);
  });
  useTitle(data?.task?.title || "Tasks");
  const isMobile = useIsMobile();
  useEffect(() => {
    if (addTask) {
      let newTask = new Task({ state: 1 });
      if (inspiredTask) {
        newTask.title = inspiredTask.title;
        newTask.sourceEntityType = "task_inspiration";
        newTask.sourceEntityRef = inspiredTask.ref;
      } else if (unownedTask) {
        newTask = { ...newTask, ...unownedTask };
      }

      setTask(newTask);
      setIsUnsavedTask(true);
      return;
    }
    if (isUnsavedTask) return;
    request({ taskRef: params.taskRef!, markTaskNotificationsRead: true });
  }, [params.taskRef, inspiredTask]);

  useEffect(() => {
    if (task?.family?.ref && !isUnsavedTask) {
      requestFamilyDetails({ familyRef: task.family.ref });
    } else if (family?.ref && isUnsavedTask) {
      requestFamilyDetails({ familyRef: family.ref });
    }
  }, [task?.family?.ref, family?.ref, isUnsavedTask]);

  if (error) return <NotFound title={"Task not found"} />;
  if (!task || (!task && loading)) return <Loading />;

  const breadcrumbs =
    isUnsavedTask && !family
      ? [{ name: "Tasks", back: true }]
      : [
          {
            name: isUnsavedTask && family ? family.name : task?.family?.name,
            link: `/families/${encodeURIComponent(
              isUnsavedTask && family ? family.ref : task?.family?.ref || "",
            )}`,
          },
          { name: "Tasks", back: true },
        ];
  const taskContent = (
    <>
      <Box
        display="flex"
        flexDirection="row"
        maxWidth="850px"
        sx={{ marginBottom: "8px" }}
      >
        <Box display="flex" flexDirection="column" width="100%">
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {task?.ref && (
                <TaskProjectLabels
                  isMember={false}
                  initialTask={task}
                  editable={true}
                  onUpdate={(updatedTask) => {
                    setTask(updatedTask);
                  }}
                />
              )}
              {(task?.ref || isUnsavedTask) && (
                <TaskDropDown
                  task={task}
                  refresh={() => {
                    request({
                      taskRef: task?.ref!,
                      markTaskNotificationsRead: false,
                    });
                  }}
                  isUnsavedTask={isUnsavedTask}
                  toggleHiddenUnsavedTask={(hidden) => {
                    setTask((t) => {
                      if (t) {
                        t.hidden = hidden;
                      }
                      return t;
                    });
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <TaskDetailsPanel
          key={task.ref}
          task={task}
          family={isUnsavedTask ? family : data?.family}
          updateTask={(t) => {
            setTask(t);
            setIsUnsavedTask(false);
          }}
          isUnsavedTask={isUnsavedTask}
          refreshTask={async () => {
            const resp = await request({
              taskRef: params.taskRef!,
              markTaskNotificationsRead: false,
            });
            if (resp?.task) {
              setTask(resp.task);
            }
          }}
        />
      </Box>
    </>
  );

  const addBookmark = async (entry: FeedEntry) => {
    if (!task?.ref || !entry?.ref) return;
    await updateBookmarks({
      bookmarks: [
        {
          taskRef: task?.ref,
          entityType: entry?.medium?.toString(),
          entityRef: entry?.ref,
          add: true,
          remove: false,
        },
      ],
    });
    request({
      taskRef: task?.ref!,
      markTaskNotificationsRead: false,
    });
  };
  const communicationCallbacks = {
    onEmailSent: addBookmark,
    // These two at least seem to cause more confusion than help.
    onTextMessageSent: () => {},
    onPhoneCallStarted: () => {},
  };

  if (isMobile) {
    return (
      <Box
        sx={{
          margin: isMobile ? "" : "0 auto",
          maxWidth: "850px",
          padding: isMobile ? "20px" : "64px 0",
        }}
      >
        {taskContent}
      </Box>
    );
  }
  return (
    <>
      <TwoPanelGrid
        defaultPanel={Panel.Left}
        leftPanel={{
          title: <Typography variant="h3">{task?.title || "Task"}</Typography>,
          subtitle: !isMobile ? (
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          ) : null,
          switchContent: <Typography variant="body">Task details</Typography>,
          content: (
            <Box
              sx={{
                maxWidth: "850px",
                padding: isMobile ? "20px 0" : "0",
                alignSelf: "center",
                width: "100%",
              }}
            >
              {taskContent}
            </Box>
          ),
        }}
        rightPanel={
          familyData?.family
            ? {
                sx: { height: "100%" },
                title: null,
                subtitle: isMobile ? (
                  <Box>
                    <Typography variant="h1Serif">
                      {familyData.family.name}
                    </Typography>
                  </Box>
                ) : null,
                switchContent: (
                  <Typography variant="body">Activity feed</Typography>
                ),
                content: (
                  <ActivityFeed
                    family={familyData.family}
                    suggestionActions={{
                      suggestTask: (t) => {
                        actionsRef.current?.suggestTask(t);
                      },
                      suggestFact: (f) => {
                        actionsRef.current?.suggestFact(f);
                      },
                      suggestEvent: (e) => {
                        actionsRef.current?.suggestEvent(e);
                      },
                    }}
                    primaryAdvisor={familyData.primaryAdvisor}
                    advisorPermissions={familyData.advisorPermissions}
                    selectedMedium={selectedMedium}
                    communicationCallbacks={communicationCallbacks}
                  />
                ),
              }
            : undefined
        }
      />
      {task?.family?.ref && (
        <SuggestionActions ref={actionsRef} familyRef={task?.family?.ref} />
      )}
    </>
  );
};
